/**
 * Transcript Request Components
 * Manages transcript copy requests from students and handling by teachers
 */

import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

/**
 * Custom hook for managing Transcript Request state and handlers
 */
export const useTranscriptRequestManagement = ({ currentUser, userProfile }) => {
  const [transcriptRequests, setTranscriptRequests] = useState([]);
  const [newTranscriptRequest, setNewTranscriptRequest] = useState({
    purpose: '',
    additionalInfo: ''
  });

  // Listen for transcript requests
  useEffect(() => {
    if (!currentUser) return;

    const qTranscriptRequests = query(collection(db, 'transcriptRequests'), orderBy('timestamp', 'desc'));
    const unsubscribeTranscriptRequests = onSnapshot(qTranscriptRequests, (snapshot) => {
      const transcriptRequestsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTranscriptRequests(transcriptRequestsData);
    }, (error) => console.error("Error fetching transcript requests:", error));

    return () => unsubscribeTranscriptRequests();
  }, [currentUser]);

  // Submit a new transcript request (student)
  const handleSubmitTranscriptRequest = async () => {
    if (!newTranscriptRequest.purpose.trim()) {
      alert('Please fill in the purpose field.');
      return;
    }

    try {
      await addDoc(collection(db, 'transcriptRequests'), {
        studentId: currentUser.uid,
        studentName: userProfile?.name || 'Unknown Student',
        studentEmail: currentUser.email,
        purpose: newTranscriptRequest.purpose.trim(),
        additionalInfo: newTranscriptRequest.additionalInfo.trim(),
        status: 'pending',
        timestamp: new Date().toISOString(),
        completedAt: null
      });
      
      setNewTranscriptRequest({
        purpose: '',
        additionalInfo: ''
      });
      
      alert('Transcript request submitted successfully!');
    } catch (error) {
      console.error('Error submitting transcript request:', error);
      alert('Failed to submit request. Please try again.');
    }
  };

  // Mark a request as complete (teacher)
  const handleCompleteTranscriptRequest = async (requestId) => {
    try {
      await updateDoc(doc(db, 'transcriptRequests', requestId), {
        status: 'completed',
        completedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error completing transcript request:', error);
      alert('Failed to mark request as complete. Please try again.');
    }
  };

  // Delete a request
  const handleDeleteTranscriptRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this transcript request?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'transcriptRequests', requestId));
    } catch (error) {
      console.error('Error deleting transcript request:', error);
      alert('Failed to delete request. Please try again.');
    }
  };

  return {
    transcriptRequests,
    newTranscriptRequest,
    setNewTranscriptRequest,
    handleSubmitTranscriptRequest,
    handleCompleteTranscriptRequest,
    handleDeleteTranscriptRequest
  };
};

/**
 * Transcript Request Form Component (Student)
 */
export const TranscriptRequestForm = ({ newTranscriptRequest, setNewTranscriptRequest, handleSubmitTranscriptRequest }) => {
  return (
    <div className="create-post">
      <h2>Request Transcript Copy</h2>
      <p className="transcript-description">
        Request a copy of your official transcript. This will notify your teachers to prepare and send your transcript.
      </p>
      
      <div className="form-group">
        <label>Purpose: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <textarea
          placeholder="What do you need the transcript for? (e.g., college application, scholarship, internship, etc.)"
          value={newTranscriptRequest.purpose}
          onChange={(e) => setNewTranscriptRequest({ ...newTranscriptRequest, purpose: e.target.value })}
          className="post-description-input"
          rows="4"
        />
      </div>

      <div className="form-group">
        <label>Additional Information (Optional)</label>
        <textarea
          placeholder="Any specific requirements or additional details (e.g., specific format, deadline, etc.)"
          value={newTranscriptRequest.additionalInfo}
          onChange={(e) => setNewTranscriptRequest({ ...newTranscriptRequest, additionalInfo: e.target.value })}
          className="post-description-input"
          rows="3"
        />
      </div>

      <button 
        onClick={handleSubmitTranscriptRequest} 
        className="class-assignment-button"
        disabled={!newTranscriptRequest.purpose.trim()}
      >
        Submit Request
      </button>
    </div>
  );
};

/**
 * Student's Own Transcript Requests Display Component
 */
export const TranscriptRequestsDisplay = ({ transcriptRequests, currentUser }) => {
  const myRequests = transcriptRequests.filter(req => req.studentId === currentUser.uid);
  
  if (myRequests.length === 0) {
    return null;
  }

  return (
    <div className="post">
      <h3>Your Transcript Requests</h3>
      <div className="mentor-requests-list">
        {myRequests.map((request) => {
          return (
            <div key={request.id} className="mentor-request-item">
              <div className="mentor-request-header">
                <strong className="mentor-request-status">
                  Transcript Request
                  {request.status === 'completed' && (
                    <span className="mentor-request-completed-badge">
                      <i className="fa-solid fa-check"></i> Completed
                    </span>
                  )}
                </strong>
                <span className="timestamp">
                  Requested: {new Date(request.timestamp).toLocaleDateString()}
                </span>
              </div>
              <div className="mentor-request-description">
                <strong>Purpose:</strong> {request.purpose}
              </div>
              {request.additionalInfo && (
                <div className="mentor-request-description">
                  <strong>Additional Information:</strong> {request.additionalInfo}
                </div>
              )}
              <div className="mentor-request-email">
                Requested on {new Date(request.timestamp).toLocaleDateString()}
              </div>
              {request.status === 'completed' && request.completedAt && (
                <div className="lor-request-complete-info">
                  <i className="fa-solid fa-check lor-request-check-icon"></i>
                  Request completed {new Date(request.completedAt).toLocaleDateString()}
                </div>
              )}
              {request.status === 'pending' && (
                <div className="mentor-request-email" style={{marginTop: 'var(--spacing-sm)', fontStyle: 'italic'}}>
                  Status: Pending review
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Teacher's Transcript Requests Display Component
 */
export const TeacherTranscriptRequestsDisplay = ({ transcriptRequests, onComplete, onDelete }) => {
  const pendingRequests = transcriptRequests.filter(request => request.status === 'pending');
  const completedRequests = transcriptRequests.filter(request => request.status === 'completed');

  return (
    <div className="post post-margin-bottom">
      <h2>Transcript Requests</h2>
      <p className="transcript-description">
        Review and manage transcript copy requests from students.
      </p>

      {transcriptRequests.length === 0 ? (
        <p className="no-posts">No transcript requests yet.</p>
      ) : (
        <>
          {/* Pending Requests */}
          {pendingRequests.length > 0 && (
            <>
              <h3 style={{ color: 'var(--color-primary)', marginTop: '20px', marginBottom: '15px' }}>Pending Requests</h3>
              {pendingRequests.map((request) => {
                return (
                  <div key={request.id} id={`transcript-request-${request.id}`} className="lor-request-item">
                    <div className="lor-request-header">
                      <div>
                        <h4 className="lor-request-student-name">
                          {request.studentName}
                        </h4>
                        <p className="lor-request-email">{request.studentEmail}</p>
                      </div>
                      <div className="lor-request-deadline">
                        <strong>Requested:</strong> {new Date(request.timestamp).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="lor-request-body">
                      <div className="lor-request-field">
                        <strong>Purpose:</strong>
                        <p>{request.purpose}</p>
                      </div>

                      {request.additionalInfo && (
                        <div className="lor-request-field">
                          <strong>Additional Information:</strong>
                          <p>{request.additionalInfo}</p>
                        </div>
                      )}

                      <div className="lor-request-timestamp">
                        Requested on {new Date(request.timestamp).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="lor-request-actions">
                      <button 
                        onClick={() => onComplete(request.id)} 
                        className="save-btn"
                      >
                        Mark as Complete
                      </button>
                      <button 
                        onClick={() => onDelete(request.id)} 
                        className="delete-btn"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Completed Requests */}
          {completedRequests.length > 0 && (
            <>
              <h3 style={{ color: 'var(--color-text-tertiary)', marginTop: '40px', marginBottom: '15px' }}>Completed Requests</h3>
              {completedRequests
                .sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0))
                .map((request) => (
                  <div key={request.id} id={`transcript-request-${request.id}`} className="lor-request-item lor-request-completed">
                    <div className="lor-request-header">
                      <div>
                        <h4 className="lor-request-student-name">{request.studentName}</h4>
                        <p className="lor-request-email">{request.studentEmail}</p>
                      </div>
                      <div className="lor-request-deadline">
                        <strong>Completed:</strong> {new Date(request.completedAt).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="lor-request-body">
                      <div className="lor-request-field">
                        <strong>Purpose:</strong>
                        <p>{request.purpose}</p>
                      </div>

                      {request.additionalInfo && (
                        <div className="lor-request-field">
                          <strong>Additional Information:</strong>
                          <p>{request.additionalInfo}</p>
                        </div>
                      )}

                      <div className="lor-request-timestamp">
                        Requested on {new Date(request.timestamp).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="lor-request-actions">
                      <button 
                        onClick={() => onDelete(request.id)} 
                        className="delete-btn"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
            </>
          )}
        </>
      )}
    </div>
  );
};

